import { gql, TypedDocumentNode } from "@apollo/client";
import {
  ADDRESS_FRAGMENT,
  PHONE_NUMBER_FRAGMENT,
  MEMBERSHIP_BAND_FRAGMENT,
  CCMS_SUSPENSION_FRAGMENT,
  PROFILE_DOCUMENT_REFERENCE_FIELDS_FRAGMENT
} from "@/fragments";
import { Address, CRMNoteReference } from "@/types";

export type AddressUpdate = Omit<Address, "id">;

export const PERSON_DETAILS_HEADER_QUERY = gql`
  query PersonDetailsHeader($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      firstName
      middleName
      lastName
      displayName
      informalName
      preferredLastName
      suffixName
      profilePhoto
      membershipSince
      primarySection {
        id
        name
      }
      employments {
        id
        isPrimary
        title
        formattedTitle
        startedOn
        endedOn
        jobClassification
        intendedJobClassification
        facility {
          id
          name
          addresses {
            id
            city
            state
            addressType
          }
        }
      }
    }
  }
`;

export const PERSON_DETAILS_HEADER_EDUCATION_QUERY = gql`
  query PersonDetailsHeaderEducation($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      university {
        id
        legacyId
        name
      }
    }
  }
`;

export const PERSON_DETAILS_HEADER_MEMBERSHIP_QUERY = gql`
  query PersonDetailsHeaderMembership($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      latestProgram {
        id
        status
        programType {
          id
          name
        }
        band {
          ...MembershipBandFragment
        }
      }
      ccmsSuspensions {
        ...CcmsSuspensionFragment
      }
    }
  }
  ${MEMBERSHIP_BAND_FRAGMENT}
  ${CCMS_SUSPENSION_FRAGMENT}
`;

export const PERSON_CONTACT_CHROME_QUERY = gql`
  query PersonContactChrome($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      email
      phoneNumbers {
        ...PhoneNumberFragment
      }
    }
  }
  ${PHONE_NUMBER_FRAGMENT}
`;

type UpdatePersonInput = { id: string; addresses?: AddressUpdate[] };

export const UPDATE_ADDRESSES_MUTATION: TypedDocumentNode<
  {
    updatePerson: {
      success: boolean;
      message: string;
      person: {
        addresses: Address[];
        contactAddress: Address;
        contactAddressId: string;
      };
    };
  },
  { input: UpdatePersonInput }
> = gql`
  mutation UpdateAddresses($input: BackofficeUpdatePersonInput!) {
    updatePerson(input: $input) {
      success
      message
      person {
        id
        addresses {
          ...AddressFragment
        }
        contactAddress {
          ...AddressFragment
        }
        contactAddressId
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export const UPDATE_PERSON_MUTATION = gql`
  mutation UpdatePerson($input: BackofficeUpdatePersonInput!) {
    updatePerson(input: $input) {
      success
      message
      code
      person {
        id
        firstName
        middleName
        lastName
        suffixName
        informalName
        preferredLastName
        spouseName
        displayName
        email
        gender
        age
        profilePhoto
        dob {
          day
          month
          year
        }
        ssn {
          full
          last4
        }
        phoneNumbers {
          ...PhoneNumberFragment
        }
        nameChangeDocument {
          id
          name
          category
          filename
          url
          createdAt
          updatedAt
        }
      }
    }
  }
  ${PHONE_NUMBER_FRAGMENT}
`;

export const UPDATE_CONTACT_PREFERENCE_MUTATION = gql`
  mutation UpdateContactPreference($input: BackofficeUpdatePersonInput!) {
    updatePerson(input: $input) {
      success
      message
      person {
        id
        contactAddress {
          ...AddressFragment
        }
        contactAddressId
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export const SECTION_CHAPTER_OPTIONS_QUERY = gql`
  query SectionChapterOptions($sectionId: ID!) {
    section(id: $sectionId) {
      id
      codedChapters {
        id
        code
        name
      }
    }
  }
`;

export const SECTION_CHAPTER_OPTIONS_BY_SECTION_IDS_QUERY = gql`
  query SectionChapterOptionsBySectionIds($sectionIds: [ID!]) {
    sections(ids: $sectionIds) {
      id
      codedChapters {
        id
        code
        name
      }
    }
  }
`;

const PERSON_SECTION_CHAPTER_AFFILIATION_FIELDS = `
  id
  primarySectionAffiliation {
    id
    primary
    startAt
    endAt
    chapterAffiliations {
      id
      attributeCode
      chapter {
        id
        code
        name
      }
      createdAt
      createdBy
    }
    chapterAffiliationDescription
    section {
      id
      name
    }
  }
  sectionAffiliations {
    id
    primary
    startAt
    endAt
    chapterAffiliations {
      id
      attributeCode
      chapter {
        id
        code
        name
      }
      createdAt
      createdBy
    }
    chapterAffiliationDescription
    section {
      id
      name
    }
  }
`;

export const CREATE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION = gql`
  mutation CreatePersonSectionChapterAffiliation($input: BackofficeCreatePersonSectionChapterAffiliationInput!) {
    createPersonSectionChapterAffiliation(input: $input) {
      success
      message
      person {
        ${PERSON_SECTION_CHAPTER_AFFILIATION_FIELDS}
      }
    }
  }
`;

export const UPDATE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION = gql`
  mutation UpdatePersonSectionChapterAffiliation($input: BackofficeUpdatePersonSectionChapterAffiliationInput!) {
    updatePersonSectionChapterAffiliation(input: $input) {
      success
      message
      person {
        ${PERSON_SECTION_CHAPTER_AFFILIATION_FIELDS}
      }
    }
  }
`;

export const DELETE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION = gql`
  mutation DeletePersonSectionChapterAffiliation($input: BackofficeDeletePersonSectionChapterAffiliationInput!) {
    deletePersonSectionChapterAffiliation(input: $input) {
      success
      message
      person {
        ${PERSON_SECTION_CHAPTER_AFFILIATION_FIELDS}
      }
    }
  }
`;

export type PersonProfileDocumentCategoryOption = {
  value: string;
  label: string;
  uploadable: boolean;
};

export type PersonProfileDocumentsQueryResponse = {
  person: {
    id: string;
    profileDocuments: CRMNoteReference[];
  };
  personProfileDocumentCategories: PersonProfileDocumentCategoryOption[];
};

export type PersonProfileDocumentsQueryVariables = {
  pgaId: string;
};

export type PersonNoteDocumentUploadContextQueryResponse = {
  person: {
    id: string;
  };
  personProfileDocumentCategories: PersonProfileDocumentCategoryOption[];
};

export const PERSON_NOTE_DOCUMENT_UPLOAD_CONTEXT_QUERY: TypedDocumentNode<
  PersonNoteDocumentUploadContextQueryResponse,
  PersonProfileDocumentsQueryVariables
> = gql`
  query PersonNoteDocumentUploadContext($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
    }
    personProfileDocumentCategories {
      value
      label
      uploadable
    }
  }
`;

export const PERSON_PROFILE_DOCUMENTS_QUERY: TypedDocumentNode<
  PersonProfileDocumentsQueryResponse,
  PersonProfileDocumentsQueryVariables
> = gql`
  query PersonProfileDocuments($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      profileDocuments {
        __typename
        ...ProfileDocumentReferenceFields
      }
    }
    personProfileDocumentCategories {
      value
      label
      uploadable
    }
  }
  ${PROFILE_DOCUMENT_REFERENCE_FIELDS_FRAGMENT}
`;
